import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  toCatalogItems,
  type Exercise,
  type ExerciseCatalogReader,
} from "@heraion/shared";
import type { CatalogItem } from "../ProgramEditor/types";

// ONE catalog hook for every picker surface (anti-monster S-C): loads
// the complete exercise catalog through the host's catalog source
// (cache-first, so offline gyms still get a list) and resolves names to
// the active locale. Locale switches re-resolve without a refetch.

export type UseCatalogItemsOptions = {
  catalog: ExerciseCatalogReader;
};

export function useCatalogItems(opts: UseCatalogItemsOptions): {
  items: CatalogItem[];
  /** The unresolved catalog rows — for surfaces that need more than
   *  {slug, name} (e.g. ProgramPreview reads primary muscles). */
  raw: ReadonlyArray<Exercise> | null;
  ready: boolean;
} {
  const { i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const [raw, setRaw] = useState<ReadonlyArray<Exercise> | null>(null);

  // Refs so a host passing fresh literals per render can't re-trigger
  // the mount effect (the unstable-object-dep lesson).
  const sourceRef = useRef(opts.catalog);
  useEffect(() => {
    sourceRef.current = opts.catalog;
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = await sourceRef.current.loadCached();
      if (!cancelled && cached != null) setRaw(cached);
      try {
        const fresh = await sourceRef.current.fetchAndPersist();
        if (cancelled) return;
        setRaw(fresh);
      } catch {
        // Offline / fetch failure: the cached snapshot (or empty list)
        // stands; the next mount retries.
        if (!cancelled) setRaw((prev) => prev ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(
    () => (raw ? toCatalogItems(raw, locale) : []),
    [raw, locale],
  );

  return { items, raw, ready: raw !== null };
}
